import { GlobalStyles, SxProps, Theme } from '@mui/material';
import { FC } from 'react';

const RING_ANIMATION = 'tp-orbit 4.2s linear infinite';

/** `--a` must be a registered property or the gradient never turns; rendered once by the theme provider. */
export const AgentOrbitRingStyles: FC = () => (
  <GlobalStyles
    styles={`
      @property --a {
        syntax: '<angle>';
        inherits: false;
        initial-value: 0deg;
      }

      @keyframes tp-orbit {
        to { --a: 360deg; }
      }
    `}
  />
);

/** Marks a button as the AI-assisted way in (design option 7): a short accent arc orbits its outline every 4.2s. */
export const agentOrbitRingSx: SxProps<Theme> = {
  position: 'relative',
  borderRadius: '999px',
  // MUI gives a Button with a startIcon a zero-width `::before` for baseline alignment; the ring needs its full box.
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '-1px',
    width: 'auto',
    borderRadius: '999px',
    padding: '1px',
    background:
      'conic-gradient(from var(--a, 0deg), transparent 0deg, transparent 350deg, #6B79FB 355deg, #6B79FB 360deg)',
    WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
    WebkitMaskComposite: 'xor',
    mask: 'linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)',
    animation: RING_ANIMATION,
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: '-4px',
    borderRadius: '999px',
    background:
      'conic-gradient(from var(--a, 0deg), transparent 0deg, transparent 352deg, rgba(107, 121, 251, 0.5) 360deg)',
    filter: 'blur(4px)',
    animation: RING_ANIMATION,
    opacity: 0.6,
    pointerEvents: 'none',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&::before, &::after': {
      animation: 'none',
    },
  },
};

/** The ring on an outlined button needs an opaque pill under it, so the glow never shows through the surface. */
export const agentOrbitOutlinedSx: SxProps<Theme> = [
  agentOrbitRingSx,
  (theme) => ({
    color: theme.vars.palette.text.primary,
    backgroundColor: theme.vars.palette.background.paper,
    borderColor: theme.vars.palette.divider,
    '&:hover': {
      backgroundColor: theme.vars.palette.background.paper,
    },
  }),
];
